import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Briefcase, Check, ChevronDown, MapPin, X } from 'lucide-react';
import { JobPosting, JOB_STATUSES, JOB_TYPES } from '@/services/job.service';
import { useSharedData } from '@/hooks/useSharedData';
import { Department } from '@/services/department.service';
import { getDepartments } from '@/services/department.service';
import { JobStatus } from '@/types/models';

interface AddJobDialogProps {
  onAddJob?: (job: JobPosting) => void;
  initialData?: JobPosting;
  isEdit?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function AddJobDialog({
  onAddJob,
  initialData,
  isEdit = false,
  open: controlledOpen,
  onOpenChange
}: AddJobDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const { departments: sharedDepartments } = useSharedData();
  const [departments, setDepartments] = useState<Department[]>([]);

  // Use controlled or uncontrolled state based on props
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange || setUncontrolledOpen;

  const LOCATION_OPTIONS = ['Ahmedabad', 'Surat'];
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    title: '',
    department: '' as string | { _id?: string; name?: string },
    locations: [] as string[],
    salaryRangeFrom: 1000,
    salaryRangeTo: 1000,
    description: '',
    jobType: 'FULL_TIME' as 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERNSHIP',
    status: JobStatus.OPEN as string,
    openPositions: 1,
  });

  // Close location dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(e.target as Node)) {
        setLocationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (initialData) {
      // Parse location string into array
      const locStr = initialData.location || '';
      const locs = locStr.split(',').map(l => l.trim()).filter(Boolean);
      setForm({
        title: initialData.title,
        department: initialData.department,
        locations: locs,
        salaryRangeFrom: initialData.salaryRangeFrom,
        salaryRangeTo: initialData.salaryRangeTo,
        description: initialData.description,
        jobType: initialData.jobType,
        status: typeof initialData.status === 'string'
          ? initialData.status
          : (initialData.status as any)?.value || (initialData.status as any)?.name || 'OPEN',
        openPositions: initialData.openPositions || 1,
      });
    }
  }, [initialData]);

  // Load departments - use shared data if available, otherwise fetch directly
  useEffect(() => {
    const loadDepartments = async () => {
      if (sharedDepartments && sharedDepartments.length > 0) {
        setDepartments(sharedDepartments);
      } else {
        try {
          const response = await getDepartments();
          const departmentsData = response.data?.data || response.data || [];
          setDepartments(departmentsData);
        } catch (error) {
          console.error('Error fetching departments:', error);
          setDepartments([]);
        }
      }
    };

    loadDepartments();
  }, [sharedDepartments]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'salaryRangeFrom' || name === 'salaryRangeTo' || name === 'openPositions') {
      const numValue = parseInt(value);
      // For openPositions, don't allow zero, keep previous value if zero is entered
      if (name === 'openPositions' && numValue === 0) {
        return; // Don't update the form
      }
      setForm((prev) => ({ ...prev, [name]: numValue || 0 }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleStatusChange = (value: string) => {
    setForm((prev) => ({ ...prev, status: value }));
  };

  const handleJobTypeChange = (value: string) => {
    setForm((prev) => ({ ...prev, jobType: value as 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERNSHIP' }));
  };

  const handleDepartmentChange = (value: string) => {
    setForm((prev) => ({ ...prev, department: value }));
  };

  const toggleLocation = (loc: string) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.includes(loc)
        ? prev.locations.filter(l => l !== loc)
        : [...prev.locations, loc]
    }));
  };

  const removeLocation = (loc: string) => {
    setForm((prev) => ({ ...prev, locations: prev.locations.filter(l => l !== loc) }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (onAddJob) {
      const { locations, ...rest } = form;
      const jobData: JobPosting = {
        ...rest,
        location: locations.join(', '),
        status: typeof form.status === 'string'
          ? form.status
          : (form.status as any)?.value || (form.status as any)?.name || 'OPEN',
        _id: initialData?._id,
        postedDate: initialData?.postedDate || new Date().toISOString().split('T')[0],
        applicants: initialData?.applicants || 0,
        timeline: initialData?.timeline || []
      };


      onAddJob(jobData);
    }
    if (!isEdit) {
      setForm({
        title: '',
        department: '' as string | { _id?: string; name?: string },
        locations: [],
        salaryRangeFrom: 1000,
        salaryRangeTo: 1000,
        description: '',
        jobType: 'FULL_TIME',
        status: JobStatus.OPEN,
        openPositions: 1,
      });
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button size="default" className="hover:bg-primary h-10 px-4">
            <Briefcase className="mr-2 h-4 w-4" />
            Add Job
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-4xl w-full">
        <DialogHeader className="rounded-t-2xl bg-background p-3 border-b">
          <DialogTitle className="text-xl font-semibold">
            {isEdit ? 'Edit Job' : 'Add New Job'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Edit existing job details' : 'Create a new job posting by filling out the following form.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-6">
          <form id="add-job-form" onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Job Title <span className="text-red-500">*</span></label>
              <Input
                name="title"
                placeholder="Job Title"
                value={form.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Department <span className="text-red-500">*</span></label>
              <Select value={typeof form.department === 'string' ? form.department : form.department?._id || ''} onValueChange={handleDepartmentChange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.length > 0 ? (
                    departments.map((dept: Department) => (
                      <SelectItem key={dept._id || dept.name} value={dept._id || dept.name}>
                        {dept.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>
                      No departments available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {departments.length === 0 && (
                <p className="text-sm text-gray-500">Loading departments...</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Location <span className="text-red-500">*</span></label>
              <div ref={locationRef} className="relative">
                <button
                  type="button"
                  onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                  className={`flex items-center justify-between w-full h-10 px-3 py-2 text-sm rounded-md border bg-background ring-offset-background transition-colors ${
                    locationDropdownOpen ? 'border-ring ring-2 ring-ring/20' : 'border-input'
                  }`}
                >
                  <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                    {form.locations.length === 0 ? (
                      <span className="text-muted-foreground">Select Locations</span>
                    ) : (
                      <div className="flex items-center gap-1 flex-wrap">
                        {form.locations.map(loc => (
                          <span
                            key={loc}
                            className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-md border border-indigo-200/60"
                          >
                            <MapPin className="h-3 w-3" />
                            {loc}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removeLocation(loc); }}
                              className="hover:text-red-500 transition-colors ml-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${locationDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {locationDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-md py-1">
                    {LOCATION_OPTIONS.map(loc => {
                      const isSelected = form.locations.includes(loc);
                      return (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => toggleLocation(loc)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-accent ${
                            isSelected ? 'text-indigo-700 font-medium' : 'text-foreground'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <MapPin className="h-3.5 w-3.5 text-gray-400" />
                          {loc}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Salary From <span className="text-red-500">*</span></label>
                <Input
                  name="salaryRangeFrom"
                  type="number"
                  placeholder="80000"
                  value={form.salaryRangeFrom}
                  onChange={handleChange}
                  required
                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Salary To <span className="text-red-500">*</span></label>
                <Input
                  name="salaryRangeTo"
                  type="number"
                  placeholder="120000"
                  value={form.salaryRangeTo}
                  onChange={handleChange}
                  required
                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Number of Positions <span className="text-red-500">*</span></label>
              <Input
                name="openPositions"
                type="number"
                placeholder="1"
                value={form.openPositions}
                onChange={handleChange}
                required
                min="1"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Job Description <span className="text-red-500">*</span></label>
              <Textarea
                name="description"
                placeholder="Job Description"
                value={form.description}
                onChange={handleChange}
                required
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Job Type <span className="text-red-500">*</span></label>
              <Select value={form.jobType} onValueChange={handleJobTypeChange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Job Type" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status <span className="text-red-500">*</span></label>
              <Select value={form.status} onValueChange={handleStatusChange} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-2 pt-4 px-6 pb-6">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-job-form">{isEdit ? 'Update Job' : 'Add Job'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 